import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  getMyPanelSessionState,
  PanelSessionError,
  type PanelSessionState,
} from '@/services/panelSession';

export const panelSessionKeys = {
  all: ['panel-session-state'] as const,
  detail: (userId?: string | null) => [...panelSessionKeys.all, userId ?? 'anonymous'] as const,
};

export interface UsePanelSessionStateOptions {
  enabled?: boolean;
  userId?: string | null;
}

export interface UsePanelSessionStateResult {
  data: PanelSessionState | null;
  panelSessionState: PanelSessionState | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: PanelSessionError | Error | null;
  refetch: UseQueryResult<PanelSessionState, PanelSessionError | Error>['refetch'];
  invalidate: () => Promise<void>;
}

export function usePanelSessionState(
  options: UsePanelSessionStateOptions = {},
): UsePanelSessionStateResult {
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? true;

  const query = useQuery<PanelSessionState, PanelSessionError | Error>({
    queryKey: panelSessionKeys.detail(options.userId),
    queryFn: getMyPanelSessionState,
    enabled,
    staleTime: 0,
    gcTime: 5 * 60_000,
    retry: (failureCount, error) => {
      if (error instanceof PanelSessionError) {
        return false;
      }

      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: query.data ?? null,
    panelSessionState: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error ?? null,
    refetch: query.refetch,
    invalidate: async () => {
      await queryClient.invalidateQueries({ queryKey: panelSessionKeys.all });
    },
  };
}